import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ipezbyApiMethods } from '@/api/ipezby/api';
import type { DnsRecordType, HostInfoResult } from '@/api/ipezby/types';

/**
 * Hook для получения IP клиента (при загрузке страницы)
 */
export function useClientIp() {
  return useQuery({
    queryKey: ['ipezby', 'clientIp'],
    queryFn: () => ipezbyApiMethods.getClientIp(),
    staleTime: 5 * 60_000, // 5 минут
    retry: 1,
  });
}

/**
 * Hook для запроса информации о хосте
 */
export function useHostInfo() {
  return useMutation({
    mutationFn: async (host: string): Promise<HostInfoResult> => {
      const isIp = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host);

      // Запрашиваем WHOIS
      const whoisPromise = ipezbyApiMethods.getWhois(host);

      // Для IP - PTR записи, для доменов - DNS записи
      let dnsPromises: Promise<{ type: string; info: string }>[];

      if (isIp) {
        dnsPromises = [
          ipezbyApiMethods
            .getPtr(host)
            .then((r) => ({ type: 'PTR', info: r.info })),
        ];
      } else {
        const dnsTypes: DnsRecordType[] = ['a', 'ns', 'mx', 'txt'];
        dnsPromises = dnsTypes.map((type) =>
          ipezbyApiMethods
            .getDns(host, type)
            .then((r) => ({ type: type.toUpperCase(), info: r.info }))
        );
      }

      const [whois, ...dnsResults] = await Promise.all([
        whoisPromise,
        ...dnsPromises,
      ]);

      return {
        host,
        isIp,
        whois,
        dnsRecords: dnsResults,
      };
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка запроса';
      toast.error('Ошибка', { description: message });
    },
  });
}
